import { useFocusable, FocusContext } from "@noriginmedia/norigin-spatial-navigation";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useCallback, useMemo } from "react";
import { getOptimizedImageForType } from "../../utils/cms.utils";
import ContentTag from "../../utils/tag";
import { FOCUS_BORDER } from "../../config/focusStyles";
import OptimizedImage from "../common/OptimizedImage";
import { useInputMode } from "../../utils/hooks/useInputMode";
import LazyRow from "../common/LazyRow";

const ContentRowScrollingContent = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
`;

const CardWrapper = styled.div`
  width: 90%;
  aspect-ratio: 9 / 13;
  position: relative;
  overflow: hidden;
  z-index: ${props => props.$focused ? 10 : 0};
  margin: 2px;
  cursor: pointer;
  border-radius: 16px;
  margin-bottom: 28px;
`;

const FocusBorderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: ${props => props.$focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive};
  border-radius: inherit;
  pointer-events: none;
  z-index: 20;
`;

const ContentOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${props => props.$focused ? 1 : 0};
  pointer-events: none;
  transition: opacity 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 8px;
  flex-direction: column;
  gap: 1rem;
  z-index: 15;
`;

const Title = styled.span`
  color: white;
  font-size: 20px;
  font-weight: 700;
  text-align: center;
`;

const Year = styled.span`
  color: white;
  font-size: 16px;
  font-weight: 400;
  text-align: center;
`;

const MovieItem = ({ item = {}, onFocus }) => {
  const navigate = useNavigate();
  const { contentPhotos, tags, slug, contentType, _id } = item;
  const { focused, ref, focusSelf } = useFocusable({
    onFocus,
    onEnterPress: () => {
      navigate(`/${contentType?.kind}/${slug}`);
    },
    focusKey: "content_" + _id,
  });

  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  // Get optimized image with LQIP and WebP support (w=200, q=90 for grid cards)
  const imageData = getOptimizedImageForType("verticalCover", contentPhotos, "TV", { width: 350, quality: 90, format: 'jpeg' });

  return (
    <CardWrapper
      ref={ref}
      $focused={focused}
      onClick={() => navigate(`/${contentType?.kind}/${slug}`)}
      onMouseEnter={handleMouseEnter}
    >
      <OptimizedImage
        src={imageData.src}
        className="absolute inset-0 w-full h-full"
        style={{
          objectFit: 'cover',
          borderRadius: "inherit",
        }}
      />
      {tags?.[0] && <ContentTag label={tags?.[0]} />}
      <FocusBorderOverlay $focused={focused} />
      <ContentOverlay $focused={focused}>
        <Title>{item.title}</Title>
        <Year>{item.year}</Year>
      </ContentOverlay>
    </CardWrapper>
  );
};

var ITEMS_PER_ROW = 5;

function chunkArray(array, size) {
  var result = [];
  for (var i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

export const CategoriesSlider = ({ movies, onFocus }) => {
  const navigate = useNavigate();
  const { ref, focusKey } = useFocusable();

  var chunkedItems = useMemo(
    function () {
      if (!movies || movies.length === 0) return [];
      return chunkArray(movies, ITEMS_PER_ROW);
    },
    [movies]
  );

  if (!movies || movies.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref}>
        {chunkedItems.map(function (rowItems, rowIndex) {
          return (
            <LazyRow key={"moftabakh_row_" + rowIndex} index={rowIndex}>
              <ContentRowScrollingContent>
                {rowItems.map((item) => (
                  <MovieItem
                    item={item}
                    key={item._id}
                    onEnterPress={() =>
                      navigate(`/${item.contentType.kind}/${item.slug}`)
                    }
                    onFocus={onFocus}
                  />
                ))}
              </ContentRowScrollingContent>
            </LazyRow>
          );
        })}
      </div>
    </FocusContext.Provider>
  );
};
